import {
  ChangeEvent, useEffect, useMemo, useState,
} from 'react';

import GetOrganisationDetails, { OrganisationDetail } from '../../services/adapters/organisationDetails';
import GetOrganisations from '../../services/adapters/organisations';
import UpdateOrganisationDetails from '../../services/adapters/updateOrganisationDetails';
import UpdateOrganisationSSO from '../../services/adapters/updateOrganisationSso';
import { formatCustomClaimClients, parseCustomClaimClients } from '../../utils/customClaimClients';
import { parseCsvInts, parseCsvStrings } from '../../utils/formatters';
import useRequestState from '../../hooks/useRequestState';
import { SamlSource, SsoFormState } from '../../types/forms.type';
import AdvancedInput from '../form-inputs/advancedInput';
import ErrorFallback from '../errorFallback';
import LoadingFallback from '../loadingFallback';
import InvalidateOrganisationCachePanel from './invalidateOrganisationCachePanel';
import OffboardOrganisationPanel from './offboardOrganisationPanel';
import { resolveSSOSaveDecision } from './resolveSSOSaveDecision';
import SsoConfigurationFields from './ssoConfigurationFields';

interface DetailsFormState {
  emailDomains: string;
  liftDataIds: string;
  isCampaignEnabled: boolean;
}

// The backend sends an empty provider name when the organisation isn't linked to an SSO configuration
function hasSSOConfig(organisationDetail: OrganisationDetail): boolean {
  return organisationDetail.samlProviderName !== '';
}

function detailsFormFromDetails(organisationDetail: OrganisationDetail): DetailsFormState {
  return {
    emailDomains: organisationDetail.emailDomains.join(', '),
    liftDataIds: organisationDetail.liftDataIds.join(', '),
    isCampaignEnabled: organisationDetail.isCampaignEnabled,
  };
}

function samlSourceFromDetails(organisationDetail: OrganisationDetail): SamlSource {
  if (organisationDetail.s3Key) return 'xml';
  if (organisationDetail.metadataUrl) return 'url';
  return 'none';
}

function ssoFormFromDetails(organisationDetail: OrganisationDetail): SsoFormState {
  return {
    samlSource: samlSourceFromDetails(organisationDetail),
    samlProviderName: organisationDetail.samlProviderName,
    metadataUrl: organisationDetail.metadataUrl ?? '',
    xmlMetadata: '',
    customClaimClients: formatCustomClaimClients(organisationDetail.customClaimClients),
  };
}

const fieldId = (name: string) => `organisation-detail-${name}`;

function EditSaveCancelButtons({
  editing,
  saving,
  disabled,
  onEdit,
  onSave,
  onCancel,
}: Readonly<{
  editing: boolean;
  saving: boolean;
  disabled?: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}>) {
  return (
    <div className="flex gap-2 justify-end">
      {!editing && (
        <button
          type="button"
          className="text-sm px-3 py-1.5 rounded-md border border-pipgrey-light text-piptext-default hover:bg-pipgrey-light disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onEdit}
          disabled={disabled}
        >
          Edit
        </button>
      )}
      {editing && (
        <>
          <button
            type="button"
            className="text-sm px-3 py-1.5 rounded-md border border-pipgrey-light text-piptext-default hover:bg-pipgrey-light disabled:opacity-50"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="text-sm px-3 py-1.5 rounded-md bg-primary-dark text-white hover:opacity-90 disabled:opacity-50"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    </div>
  );
}

export default function OrganisationManagerPanel() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrganisation, setSelectedOrganisation] = useState('');

  const {
    data: organisationNames,
    handleRequest: handleFetchNames,
    FallbackWrapper: NamesFallbackWrapper,
  } = useRequestState<string[]>();

  const {
    loading: detailsLoading,
    error: detailsError,
    handleRequest: handleFetchDetails,
  } = useRequestState<OrganisationDetail>();

  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState<DetailsFormState | null>(null);
  const [detailsSaveError, setDetailsSaveError] = useState<string | null>(null);
  const {
    loading: detailsSaving,
    handleRequest: handleDetailsSaveRequest,
  } = useRequestState<OrganisationDetail>();

  const [editingSSO, setEditingSSO] = useState(false);
  const [ssoForm, setSSOForm] = useState<SsoFormState | null>(null);
  const [ssoSaveError, setSSOSaveError] = useState<string | null>(null);
  const {
    loading: ssoSaving,
    handleRequest: handleSSOSaveRequest,
  } = useRequestState<OrganisationDetail>();

  const [committedDetails, setCommittedDetails] = useState<OrganisationDetail | null>(null);

  const applyLoadedDetails = (loaded: OrganisationDetail) => {
    setCommittedDetails(loaded);
    setDetailsForm(detailsFormFromDetails(loaded));
    setSSOForm(ssoFormFromDetails(loaded));
    setEditingDetails(false);
    setEditingSSO(false);
    setDetailsSaveError(null);
    setSSOSaveError(null);
  };

  useEffect(() => {
    void handleFetchNames(
      new GetOrganisations()
        .adaptResponse()
        .then(({ organisationNames: names }) => [...names].sort((a, b) => a.localeCompare(b))),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredNames = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return organisationNames ?? [];
    return (organisationNames ?? []).filter((name) => name.toLowerCase().includes(term));
  }, [organisationNames, searchTerm]);

  const handleOrganisationSelect = (displayName: string) => {
    if (displayName === selectedOrganisation) return;
    setSelectedOrganisation(displayName);
    setCommittedDetails(null);
    void handleFetchDetails(
      new GetOrganisationDetails().adaptResponse(displayName).then((loaded) => {
        applyLoadedDetails(loaded);
        return loaded;
      }),
    );
  };

  const handleDetailsSave = async () => {
    if (!detailsForm || !committedDetails) return;
    const original = committedDetails;

    const newEmailDomains = parseCsvStrings(detailsForm.emailDomains);
    const newLiftDataIds = parseCsvInts(detailsForm.liftDataIds);

    const payload: Record<string, unknown> = {};
    if (JSON.stringify(newEmailDomains) !== JSON.stringify(original.emailDomains)) {
      payload.emailDomains = newEmailDomains;
    }
    if (JSON.stringify(newLiftDataIds) !== JSON.stringify(original.liftDataIds)) {
      payload.liftDataIds = newLiftDataIds;
    }
    if (detailsForm.isCampaignEnabled !== original.isCampaignEnabled) {
      payload.isCampaignEnabled = detailsForm.isCampaignEnabled;
    }

    if (Object.keys(payload).length === 0) {
      setEditingDetails(false);
      return;
    }

    setDetailsSaveError(null);
    const updated = await handleDetailsSaveRequest(
      new UpdateOrganisationDetails().adaptResponse({
        organisationDisplayName: original.displayName,
        ...payload,
      }).catch((saveError) => {
        setDetailsSaveError(saveError instanceof Error ? saveError.message : 'Failed to save details.');
        throw saveError;
      }),
    );

    if (updated) applyLoadedDetails(updated);
  };

  const handleDetailsCancel = () => {
    if (committedDetails) setDetailsForm(detailsFormFromDetails(committedDetails));
    setEditingDetails(false);
    setDetailsSaveError(null);
  };

  const handleSSOSave = async () => {
    if (!ssoForm || !committedDetails) return;
    const original = committedDetails;
    const hasExistingSSOConfig = hasSSOConfig(original);

    const enteredMetadataUrl = ssoForm.samlSource === 'url' ? ssoForm.metadataUrl.trim() || null : null;
    // Unchanged metadata isn't sent, so a claims-only save doesn't touch the identity provider
    const newMetadataUrl = enteredMetadataUrl === original.metadataUrl ? null : enteredMetadataUrl;
    const newXmlMetadata = ssoForm.samlSource === 'xml' ? ssoForm.xmlMetadata.trim() || null : null;
    const newSamlProviderName = ssoForm.samlProviderName.trim();
    const newCustomClaimClients = parseCustomClaimClients(ssoForm.customClaimClients);

    const decision = resolveSSOSaveDecision({
      hasExistingSSOConfig,
      newMetadataUrl,
      newXmlMetadata,
      newSamlProviderName,
      originalCustomClaimClients: original.customClaimClients,
      newCustomClaimClients,
    });

    if (decision.kind === 'noop') {
      setEditingSSO(false);
      return;
    }
    if (decision.kind === 'invalid') {
      setSSOSaveError(decision.message);
      return;
    }

    setSSOSaveError(null);
    const updated = await handleSSOSaveRequest(
      new UpdateOrganisationSSO().adaptResponse({
        organisationDisplayName: original.displayName,
        organisationSamlProviderName: hasExistingSSOConfig ? original.samlProviderName : newSamlProviderName,
        metadataUrl: newMetadataUrl,
        xmlMetadata: newXmlMetadata,
        customClaimClients: newCustomClaimClients,
      }).catch((saveError) => {
        setSSOSaveError(saveError instanceof Error ? saveError.message : 'Failed to save SSO configuration.');
        throw saveError;
      }),
    );

    if (updated) applyLoadedDetails(updated);
  };

  const handleSSOCancel = () => {
    if (committedDetails) setSSOForm(ssoFormFromDetails(committedDetails));
    setEditingSSO(false);
    setSSOSaveError(null);
  };

  const handleOffboarded = () => {
    // Refresh the names list so the offboarded org disappears from the sidebar,
    // but keep selectedOrganisation set so OffboardOrganisationPanel stays
    // mounted and can show its done/warning state.
    void handleFetchNames(
      new GetOrganisations()
        .adaptResponse().then(({ organisationNames: names }) => [...names]),
    );
  };

  const hasExistingSSO = committedDetails ? hasSSOConfig(committedDetails) : false;

  return (
    <div className="border border-pipgrey-light rounded-md p-4 flex flex-col gap-4 flex-1 min-h-0">
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col gap-2 lg:w-80 lg:shrink-0 min-h-0">
          <input
            type="text"
            className="border border-pipgrey-light rounded-md px-3 py-2 text-sm"
            placeholder="Search organisations…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search organisations"
          />

          <NamesFallbackWrapper loadingMessage="Loading organisations…">
            <ul className="border border-pipgrey-light rounded-md flex-1 min-h-0 overflow-auto divide-y divide-pipgrey-light">
              {filteredNames.length === 0 && (
                <li className="px-3 py-2 text-sm text-piptext-default">No organisations found.</li>
              )}
              {filteredNames.map((name) => {
                const isSelected = name === selectedOrganisation;
                return (
                  <li key={name}>
                    <button
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm ${isSelected ? 'bg-primary-dark text-white' : 'text-piptext-default hover:bg-pipgrey-light'}`}
                      onClick={() => handleOrganisationSelect(name)}
                    >
                      {name}
                    </button>
                  </li>
                );
              })}
            </ul>
          </NamesFallbackWrapper>
        </div>

        <div className="flex-1 min-h-0 overflow-auto border border-pipgrey-light rounded-md p-4">
          {!selectedOrganisation && (
            <p className="text-piptext-default text-sm">Select an organisation to view its details.</p>
          )}

          {detailsLoading && <LoadingFallback message="Loading organisation details…" />}
          {!detailsLoading && detailsError && <ErrorFallback errorMessage={detailsError} />}
          {!detailsLoading && !detailsError && committedDetails && detailsForm && ssoForm && (
            <div className="flex flex-col gap-6">

              <div className="flex flex-col gap-4">
                <AdvancedInput
                  type="text"
                  id={fieldId('displayName')}
                  label="Display name"
                  tooltip="The display name of the organisation."
                  value={committedDetails.displayName}
                  readOnly
                />
                <AdvancedInput
                  type="text"
                  id={fieldId('organisationId')}
                  label="Organisation ID"
                  tooltip="Internal database ID of the organisation."
                  value={String(committedDetails.organisationId)}
                  readOnly
                />
              </div>

              <hr className="border-pipgrey-light" />

              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-primary-dark">Organisation details</h2>
                  <EditSaveCancelButtons
                    editing={editingDetails}
                    saving={detailsSaving}
                    onEdit={() => setEditingDetails(true)}
                    onSave={handleDetailsSave}
                    onCancel={handleDetailsCancel}
                  />
                </div>

                {detailsSaveError && (
                  <p className="text-pipstatus-error text-sm">{detailsSaveError}</p>
                )}

                <AdvancedInput
                  type="text"
                  id={fieldId('emailDomains')}
                  label="Email domains"
                  tooltip="Comma-separated list of email domains."
                  value={detailsForm.emailDomains}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setDetailsForm((prev) => prev && { ...prev, emailDomains: e.target.value })}
                  readOnly={!editingDetails}
                />
                <AdvancedInput
                  type="text"
                  id={fieldId('liftDataIds')}
                  label="Lift data IDs"
                  tooltip="Comma-separated list of lift data IDs."
                  value={detailsForm.liftDataIds}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setDetailsForm((prev) => prev && { ...prev, liftDataIds: e.target.value })}
                  readOnly={!editingDetails}
                />
                <AdvancedInput
                  type="checkbox"
                  id={fieldId('isCampaignEnabled')}
                  label="Campaign enabled"
                  tooltip="Whether the campaign is enabled."
                  checked={detailsForm.isCampaignEnabled}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setDetailsForm((prev) => prev && { ...prev, isCampaignEnabled: e.target.checked })}
                  disabled={!editingDetails}
                />
              </div>

              <hr className="border-pipgrey-light" />

              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-primary-dark">SSO configuration</h2>
                  <EditSaveCancelButtons
                    editing={editingSSO}
                    saving={ssoSaving}
                    onEdit={() => setEditingSSO(true)}
                    onSave={handleSSOSave}
                    onCancel={handleSSOCancel}
                  />
                </div>

                {ssoSaveError && (
                  <p className="text-pipstatus-error text-sm">{ssoSaveError}</p>
                )}

                {!editingSSO && (
                  <>
                    <AdvancedInput
                      type="text"
                      id={fieldId('samlProviderName')}
                      label="SAML provider name"
                      tooltip="Slug used as the organisation key. Must match the Cognito provider name exactly."
                      value={committedDetails.samlProviderName}
                      readOnly
                    />
                    {hasExistingSSO && (
                      <AdvancedInput
                        type="text"
                        id={fieldId('customClaimClients')}
                        label="Custom client name(s)"
                        tooltip="The custom:client SAML claim values that can sign in to this organisation."
                        value={committedDetails.customClaimClients.join(', ')}
                        readOnly
                      />
                    )}
                    {committedDetails.metadataUrl && (
                      <AdvancedInput
                        type="text"
                        id={fieldId('metadataUrl')}
                        label="SAML metadata URL"
                        tooltip="The SAML metadata URL for the organisation's identity provider."
                        value={committedDetails.metadataUrl}
                        readOnly
                      />
                    )}
                    {committedDetails.s3Key && (
                      <>
                        <AdvancedInput
                          type="text"
                          id={fieldId('s3Key')}
                          label="S3 metadata key"
                          tooltip="The S3 object key for the uploaded XML metadata file."
                          value={committedDetails.s3Key}
                          readOnly
                        />
                        <AdvancedInput
                          type="text"
                          id={fieldId('s3VersionId')}
                          label="S3 version ID"
                          tooltip="The S3 version ID of the uploaded XML metadata file."
                          value={committedDetails.s3VersionId ?? 'N/A'}
                          readOnly
                        />
                      </>
                    )}
                    {!committedDetails.metadataUrl && !committedDetails.s3Key && (
                      <p className="text-sm text-piptext-default">
                        No SSO metadata configured. Click Edit to add a SAML metadata URL or XML file.
                      </p>
                    )}
                  </>
                )}

                {editingSSO && (
                  <SsoConfigurationFields
                    idPrefix="organisation-detail"
                    isProviderNameLocked={hasExistingSSO}
                    value={ssoForm}
                    onChange={(patch) => setSSOForm((prev) => prev && { ...prev, ...patch })}
                  />
                )}

                <InvalidateOrganisationCachePanel displayName={committedDetails.displayName} />

                <OffboardOrganisationPanel
                  displayName={committedDetails.displayName}
                  onOffboarded={handleOffboarded}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
